// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Packet summary rules editor: Header fields, Rollups, and Checks built from selects (blueprint-sets Req 6.1-6.3,
// design "components/SummaryRulesEditor.tsx"). Controlled: the parent owns the SummaryRules and the Members' schemas
// at their version refs; errors come from `validateSummaryRules` on every render plus the API's 400 `fieldErrors`.
// The only free text is labels, names, a tolerance amount, and the group value of a check on a field-grouped rollup
// (whose groups only exist in documents).
import { useMemo, type ReactNode } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import {
  MAX_CHECKS,
  MAX_HEADER_FIELDS,
  MAX_ROLLUPS,
  summaryPathsOf,
  validateSummaryRules,
  type BdaSchema,
  type CheckRule,
  type HeaderFieldRule,
  type RollupRule,
  type SummaryRules,
  type ValidationErrors,
} from '@bda/shared';
import {
  type Ctx,
  ErrorText,
  isMissingRef,
  nextFree,
  normalizeApiErrors,
  removeAt,
  replaceAt,
} from './summaryRulesShared';
import { HeaderFieldRow } from './SummaryHeaderFields';
import { defaultSource, rollupMissing, RollupRow } from './SummaryRollups';
import { CheckRow, operandOptions } from './SummaryChecks';

/** A Member of the set with its schema at its version ref (null while it does not resolve). */
export interface SummaryRulesMember {
  blueprintId: string;
  /** Studio name, shown in the selects. */
  name: string;
  schema: BdaSchema | null;
}

export interface SummaryRulesEditorProps {
  value: SummaryRules;
  members: SummaryRulesMember[];
  onChange: (next: SummaryRules) => void;
  /**
   * The API's 400 `fieldErrors`, keyed as `validateSummaryRules` keys them (an optional `summaryRules.` prefix is
   * accepted). Browser validation wins at the same key, so clear these once the author edits.
   */
  fieldErrors?: ValidationErrors;
  /** A Set_Version view: every control disabled, no add or remove. */
  readOnly?: boolean;
}
// ---------- editor ----------

function Section({
  title,
  description,
  count,
  max,
  error,
  addLabel,
  addDisabledReason,
  onAdd,
  readOnly,
  empty,
  children,
}: {
  title: string;
  description: string;
  count: number;
  max: number;
  error?: string;
  addLabel: string;
  addDisabledReason?: string;
  onAdd: () => void;
  readOnly: boolean;
  empty: string;
  children: ReactNode;
}) {
  const full = count >= max;
  return (
    <Container
      header={
        <Header
          variant="h3"
          counter={`(${count}/${max})`}
          description={description}
          actions={
            !readOnly && (
              <Button iconName="add-plus" disabled={full || !!addDisabledReason} onClick={onAdd}>
                {addLabel}
              </Button>
            )
          }
        >
          {title}
        </Header>
      }
    >
      <SpaceBetween size="xs">
        <ErrorText>{error}</ErrorText>
        {!readOnly && full && <Box color="text-body-secondary">{`You can have at most ${max}.`}</Box>}
        {!readOnly && !full && addDisabledReason && <Box color="text-body-secondary">{addDisabledReason}</Box>}
        {count === 0 && <Box color="text-body-secondary">{empty}</Box>}
        {children}
      </SpaceBetween>
    </Container>
  );
}

export function SummaryRulesEditor({
  value,
  members,
  onChange,
  fieldErrors,
  readOnly = false,
}: SummaryRulesEditorProps) {
  const withSchema = useMemo(
    () => members.flatMap((m) => (m.schema ? [{ blueprintId: m.blueprintId, schema: m.schema }] : [])),
    [members],
  );
  const paths = useMemo(() => new Map(withSchema.map((m) => [m.blueprintId, summaryPathsOf(m.schema)])), [withSchema]);
  const errors = useMemo(
    () => ({ ...normalizeApiErrors(fieldErrors), ...validateSummaryRules(value, withSchema) }),
    [fieldErrors, value, withSchema],
  );
  const ctx: Ctx = { members, paths, errors, readOnly };

  // Header fields and rollups whose own reference is missing; checks that use them are marked too.
  const invalidRules = new Set<string>();
  value.headerFields.forEach((h, i) => {
    if (isMissingRef(errors[`headerFields.${i}.source`], h.source.path)) invalidRules.add(`h|${h.id}`);
  });
  value.rollups.forEach((r, i) => {
    if (rollupMissing(r, i, errors)) invalidRules.add(`r|${r.id}`);
  });

  const allIds = [...value.headerFields, ...value.rollups, ...value.checks].map((x) => x.id);
  const firstMember = members[0]?.blueprintId;
  const noMembers = firstMember ? undefined : 'Add blueprints to the set first.';

  const addHeader = () => {
    const memberId = firstMember!;
    const first = (paths.get(memberId) ?? []).find((p) => !p.table);
    const rule: HeaderFieldRule = {
      id: nextFree('h', allIds),
      label: nextFree(
        'Header field ',
        value.headerFields.map((h) => h.label),
        true,
      ),
      source: { memberId, path: first?.path ?? '' },
    };
    onChange({ ...value, headerFields: [...value.headerFields, rule] });
  };
  const addRollup = () => {
    const rule: RollupRule = {
      id: nextFree('r', allIds),
      name: nextFree(
        'Rollup ',
        value.rollups.map((r) => r.name),
        true,
      ),
      aggregate: 'sum',
      sources: [defaultSource(ctx, firstMember!, 'sum')],
    };
    onChange({ ...value, rollups: [...value.rollups, rule] });
  };
  const operands = operandOptions(value, ctx);
  const addCheck = () => {
    const left = operands[0]!.operand;
    const right = (operands[1] ?? operands[0]!).operand;
    const rule: CheckRule = {
      id: nextFree('c', allIds),
      name: nextFree(
        'Check ',
        value.checks.map((c) => c.name),
        true,
      ),
      left,
      right,
      op: 'eq',
      tolerance: { kind: 'absolute', value: 0 },
    };
    onChange({ ...value, checks: [...value.checks, rule] });
  };

  return (
    <SpaceBetween size="l">
      <Section
        title="Header fields"
        description="Fields shown at the top of the packet summary, such as an invoice number."
        count={value.headerFields.length}
        max={MAX_HEADER_FIELDS}
        error={errors.headerFields}
        addLabel="Add header field"
        addDisabledReason={noMembers}
        onAdd={addHeader}
        readOnly={readOnly}
        empty="No header fields."
      >
        {value.headerFields.map((h, i) => (
          <HeaderFieldRow
            key={i}
            rule={h}
            index={i}
            ctx={ctx}
            onChange={(r) => onChange({ ...value, headerFields: replaceAt(value.headerFields, i, r) })}
            onRemove={() => onChange({ ...value, headerFields: removeAt(value.headerFields, i) })}
          />
        ))}
      </Section>
      <Section
        title="Rollups"
        description="Totals, counts, minimums, or maximums across the documents in a packet."
        count={value.rollups.length}
        max={MAX_ROLLUPS}
        error={errors.rollups}
        addLabel="Add rollup"
        addDisabledReason={noMembers}
        onAdd={addRollup}
        readOnly={readOnly}
        empty="No rollups."
      >
        {value.rollups.map((r, i) => (
          <RollupRow
            key={i}
            rule={r}
            index={i}
            ctx={ctx}
            onChange={(next) => onChange({ ...value, rollups: replaceAt(value.rollups, i, next) })}
            onRemove={() => onChange({ ...value, rollups: removeAt(value.rollups, i) })}
          />
        ))}
      </Section>
      <Section
        title="Checks"
        description="Comparisons of header fields and rollups, such as a receipt total that should equal an invoiced amount."
        count={value.checks.length}
        max={MAX_CHECKS}
        error={errors.checks}
        addLabel="Add check"
        addDisabledReason={operands.length === 0 ? 'Add a header field or rollup first.' : undefined}
        onAdd={addCheck}
        readOnly={readOnly}
        empty="No checks."
      >
        {value.checks.map((c, i) => (
          <CheckRow
            key={i}
            rule={c}
            index={i}
            rules={value}
            ctx={ctx}
            invalidRules={invalidRules}
            onChange={(next) => onChange({ ...value, checks: replaceAt(value.checks, i, next) })}
            onRemove={() => onChange({ ...value, checks: removeAt(value.checks, i) })}
          />
        ))}
      </Section>
    </SpaceBetween>
  );
}
